import { useQuery } from '@tanstack/react-query';
import type {
  SettlementCycleEnum,
  SettlementListItem,
  SettlementRunStatusEnum,
  SettlementRunTypeEnum,
} from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import type { SettlementSort } from '@/stores/useSettlementFiltersStore';

export interface SettlementsQueryParams {
  page?: number;
  size?: number;
  sort?: SettlementSort;
  vendorId?: string | null;
  cycle?: SettlementCycleEnum | null;
  runType?: SettlementRunTypeEnum | null;
  status?: ReadonlyArray<SettlementRunStatusEnum>;
  periodFrom?: string | null;
  periodTo?: string | null;
}

export interface SettlementsPage {
  items: ReadonlyArray<SettlementListItem>;
  meta: ApiMeta;
}

interface AdminQuery {
  page?: number;
  size?: number;
  sort?: SettlementSort;
  vendorId?: string;
  cycle?: SettlementCycleEnum;
  runType?: SettlementRunTypeEnum;
  status?: SettlementRunStatusEnum[];
  periodFrom?: string;
  periodTo?: string;
}

const cleanQuery = (p: SettlementsQueryParams): AdminQuery => {
  const out: AdminQuery = {};
  out.page = p.page != null ? Math.max(0, p.page - 1) : 0;
  if (p.size) out.size = p.size;
  if (p.sort) out.sort = p.sort;
  if (p.vendorId) out.vendorId = p.vendorId;
  if (p.cycle) out.cycle = p.cycle;
  if (p.runType) out.runType = p.runType;
  if (p.status && p.status.length > 0) out.status = [...p.status];
  if (p.periodFrom) out.periodFrom = p.periodFrom;
  if (p.periodTo) out.periodTo = p.periodTo;
  return out;
};

export const settlementsQueryKey = (p: SettlementsQueryParams) =>
  ['admin', 'settlements', p] as const;

export const useSettlementsQuery = (p: SettlementsQueryParams) =>
  useQuery<SettlementsPage>({
    queryKey: settlementsQueryKey(p),
    queryFn: async () => {
      const [data, meta] = await apiClient.request('get', '/admin/v1/settlements', {
        params: { query: cleanQuery(p) },
      });
      const payload = data as { items: SettlementListItem[] };
      return { items: payload.items, meta };
    },
  });
